import { checkName, stripDisallowedNameCharacters } from "../../utility";
import { ArsText } from "../inputs/ArsText";

/**
 * Props for the NamedObjectControl component.
 */
export interface NamedObjectControlProps {
  /** Can rename the object (optional) */
  canRename?: boolean;
  /** RFC 6901 JSON Pointer path to the NamedObject in ArsContext state */
  dataPath: string;
  /** Disabled state */
  disabled?: boolean;
  /** Invalid names */
  invalidNames?: string[];
}

/**
 * A component for displaying and editing NamedObject properties (name, description, label).
 *
 * This component provides inputs for the three standard fields that are shared across many
 * ARS entities (AnalysisSet, DataSubset, Group, etc.):
 * - name: The name for the instance (required)
 * - description: A textual description (optional)
 * - label: A short informative description for display (optional)
 *
 * All changes are synchronised with ArsContext state using the PATCH_ARS_STATE action
 * through the ArsText components.
 *
 * @param props - Component props.
 * @param props.canRename - Optional flag to allow renaming the object.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the NamedObject in ArsContext state.
 * @param props.disabled - Optional flag to disable all inputs.
 * @param props.invalidNames - Optional array of invalid names to validate against.
 * @param props.nameValidationReturn - Optional callback receiving the current name validation status.
 * @returns A React functional component rendering the named object editor.
 */
export const NamedObjectControl = ({
  dataPath,
  canRename = false,
  disabled = false,
  invalidNames = [],
}: NamedObjectControlProps): React.ReactElement => {
  return (
    <div>
      <ArsText
        dataPath={`${dataPath}/name`}
        debounceMilliseconds={-1} // Only debounce this on blur
        disabled={!canRename || disabled}
        id={`${dataPath.replace(/\//g, "-")}-name`}
        label="Name"
        labelWidth={2}
        noInvalidReturn
        placeholder="Enter name"
        sanitiseValue={stripDisallowedNameCharacters}
        validCondition={checkName}
        validationTargetOptions={{ existingValues: invalidNames }}
      />
      <ArsText
        dataPath={`${dataPath}/label`}
        disabled={disabled}
        id={`${dataPath.replace(/\//g, "-")}-label`}
        isMultiline={true}
        label="Label"
        labelWidth={2}
        placeholder="Enter label"
      />
      <ArsText
        allowCarriageReturn={true}
        dataPath={`${dataPath}/description`}
        disabled={disabled}
        id={`${dataPath.replace(/\//g, "-")}-description`}
        label="Description"
        labelWidth={2}
        placeholder="Enter description"
        rows={3}
        type="textarea"
      />
    </div>
  );
};

NamedObjectControl.displayName = "NamedObjectControl";
